import { useClickOutside, useResolutionInfo } from '@mm/hooks';
import { ArrowStatefulIcon } from '@mm/icons';
import { SubscriptionFeatureFlag } from '@mm/state/controllers/FeatureFlags';
import { CompanyLevelFeatureFlag } from '@mm/state/model/CompanyModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  combinedFeatureFlagsSelector,
  getNextBillingTypeForFeature,
  getPlanFromBillingType,
  nextBillingTypeForFeatureSelector,
} from '@mm/state/selectors/featureFlagsSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classnames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { PlanBadge } from '../../../common/components/ProBadge';
import { MOBILE_THRESHOLD } from '../../../editor/constants/screenSize';
import './NavbarDropdown.pcss';

export type NavigationItem = {
  name: string;
  href: string;
  requiredFeature?: SubscriptionFeatureFlag | CompanyLevelFeatureFlag;
  description?: string;
  icon?: React.ReactNode;
};

type DropdownListProps = {
  title: string;
  url?: string;
  items: NavigationItem[];
  className?: string;
  isActive?: boolean;
  onChange?: (isOpen?: boolean) => void;
  onClick?: (title: string) => void;
  disabled?: boolean;
  requiredFeature?: SubscriptionFeatureFlag | CompanyLevelFeatureFlag;
};

type LinkProps = {
  title: string;
  href: string;
  shouldShowUpgradeModal: boolean;
  showUpgradeModal: () => void;
  icon?: React.ReactNode;
  description?: string;
};

const NavLink = ({
  title,
  href,
  shouldShowUpgradeModal,
  showUpgradeModal,
}: LinkProps) => {
  const isActive = href && window.location.pathname.includes(href);

  const className = classnames('navbar-dropdown__link', {
    'navbar-dropdown__link--active': isActive,
  });

  return !shouldShowUpgradeModal ? (
    <RouterLink className={className} to={href} title={title}>
      {title}
    </RouterLink>
  ) : (
    <button className={className} onClick={showUpgradeModal}>
      {title}
    </button>
  );
};

const NavLinkExtended = ({
  title,
  href,
  shouldShowUpgradeModal,
  showUpgradeModal,
  icon = '',
  description = '',
}: LinkProps) => {
  const isActive = href && window.location.pathname.includes(href);

  const history = useHistory();

  const className = classnames('navbar-dropdown__link-extended', {
    'navbar-dropdown__link--active': isActive,
  });

  return (
    <div
      className={className}
      onClick={() => {
        shouldShowUpgradeModal ? showUpgradeModal() : history.push(href);
      }}
    >
      <div>{icon}</div>
      <div className="navbar-dropdown__link-extended__label">{title}</div>
      <div className="navbar-dropdown__link-extended__text">{description}</div>
    </div>
  );
};

export const NavbarDropdown = ({
  className,
  title,
  items,
  onChange = () => {},
  url,
  isActive,
  disabled,
  requiredFeature,
}: DropdownListProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { openUniversalUpgradeLandmineModal } = useModalActions();

  const billingType = useSelector((state: StoreState) =>
    nextBillingTypeForFeatureSelector(state, requiredFeature),
  );

  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const featuresForPlans = useSelector(
    (state: StoreState) => state.featureFlags.featuresForPlans,
  );

  const shouldShowUpgradeModal = (item: NavigationItem) =>
    !!item.requiredFeature && !featureFlags[item.requiredFeature];

  const onClick = () => {
    if (disabled) {
      return;
    }
    setIsOpen(!isOpen);
  };

  const showUpgradeModal = (
    landmineName: string,
    requiredFeature?: CompanyLevelFeatureFlag | SubscriptionFeatureFlag,
  ) => {
    if (requiredFeature) {
      const itemBillingType = getNextBillingTypeForFeature(
        featureFlags,
        featuresForPlans,
        requiredFeature,
      );
      if (itemBillingType) {
        openUniversalUpgradeLandmineModal({
          landmineName,
          feature: requiredFeature,
        });
      }
    }
  };

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;

  const onOutsideClick = () => setIsOpen(false);

  const onTitleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const ref = useClickOutside<HTMLDivElement>(onOutsideClick);

  useEffect(onChange, [isOpen]);

  const containerClassName = classnames(className, 'navbar-dropdown-wrapper', {
    'navbar-dropdown-wrapper--with-plan-badge': !!billingType,
  });

  const linkClassName = classnames(className, 'navbar-dropdown', {
    'navbar-dropdown--active': isActive,
    'navbar-dropdown--disabled': disabled,
    'navbar-dropdown--open': isOpen,
  });

  const isMenuExtended = useMemo(
    () => !isMobile && items.some(item => item.description && item.icon),
    [items],
  );

  const menuClassName = classnames('navbar-dropdown__list-menu', {
    'navbar-dropdown__list-menu--extended': isMenuExtended,
  });

  return (
    <div
      className={containerClassName}
      ref={isActive ? ref : undefined}
      onClick={onClick}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {billingType && (
        <PlanBadge
          className="navbar-dropdown__plan-icon"
          billingType={billingType}
          size={10}
        />
      )}
      <div className={linkClassName}>
        {url ? (
          <RouterLink
            className="navbar-dropdown__button"
            to={url}
            title={title}
            onClick={onTitleClick}
          >
            {title}
          </RouterLink>
        ) : (
          <button className="navbar-dropdown__button">{title}</button>
        )}

        {items.length > 1 && (
          <span className="navbar-dropdown__angle-icon">
            <ArrowStatefulIcon
              size={16}
              color={isOpen || isActive ? '#3A50F3' : '#262626'}
              direction={isOpen ? 'up' : 'down'}
            />
          </span>
        )}

        <ul
          className={menuClassName}
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
        >
          {items.map(item => (
            <li key={item.name} className="navbar-dropdown__list-menu-item">
              {isMenuExtended ? (
                <NavLinkExtended
                  title={item.name}
                  href={item.href}
                  shouldShowUpgradeModal={shouldShowUpgradeModal(item)}
                  showUpgradeModal={() =>
                    showUpgradeModal(item.name, item.requiredFeature)
                  }
                  description={item.description}
                  icon={item.icon}
                />
              ) : (
                <NavLink
                  title={item.name}
                  href={item.href}
                  shouldShowUpgradeModal={shouldShowUpgradeModal(item)}
                  showUpgradeModal={() =>
                    showUpgradeModal(item.name, item.requiredFeature)
                  }
                />
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
